import { useEffect, useRef, useState } from "react";

type DropdownOption = {
  value: string;
  label: string;
  disabled?: boolean;
};

type FilterDropdownProps = {
  label: string;
  value: string;
  placeholder: string;
  options: DropdownOption[];
  onChange: (value: string) => void;
  error?: string | any;
  disabled?: boolean;
};

const FilterDropdown = ({
  label,
  value,
  placeholder,
  options,
  onChange,
  error,
  disabled = false,
}: FilterDropdownProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const selectedLabel =
    value && options.find((option) => option.value === value)?.label
      ? options.find((option) => option.value === value)?.label
      : placeholder;

  return (
    <div ref={containerRef} className="min-w-0 relative">
      {label && (
        <label className="mb-2 block text-[11px] font-semibold uppercase tracking-wider text-slate-500">
          {label}
        </label>
      )}
      <button
        type="button"
        onClick={() => {
  if (!disabled) {
    setIsOpen((current) => !current);
  }
}}
        className={`w-full rounded-2xl border border-slate-200 bg-slate-50 px-3 py-2.5 text-left text-sm font-medium outline-none transition flex items-center justify-between ${
  disabled
    ? "cursor-not-allowed opacity-60"
    : "text-[#0A2540] focus:border-[#117DD4]/60 focus:bg-white focus:ring-2 focus:ring-[#117DD4]/20"
}`}
        aria-expanded={isOpen}
      >
        <span className={`truncate ${!value ? "text-slate-400" : ""}`}>
          {selectedLabel}
        </span>
        <svg className={`h-4 w-4 transition-transform duration-200 ${isOpen ? "rotate-180" : ""}`} viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth={2}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M6 8l4 4 4-4" />
        </svg>
      </button>
      <div
        className={`absolute left-0 right-0 z-20 mt-2 max-h-64 overflow-y-auto rounded-2xl border border-slate-200 bg-white shadow-xl transition-all duration-200 ${isOpen ? "opacity-100 visible" : "opacity-0 invisible"
          }`}
      >
        {options.map((option) => (
          <button
            key={option.value}
            type="button"
            disabled={option.disabled}
            onClick={() => {
              if (option.disabled) return;

              onChange(option.value);
              setIsOpen(false);
            }}
            className={`w-full text-left px-4 py-2 text-sm transition flex items-center justify-between ${option.disabled
                ? "cursor-not-allowed bg-slate-100 text-slate-400"
                : option.value === value
                  ? "bg-[#0A2540] text-white"
                  : "text-slate-700 hover:bg-slate-50"
              }`}
          >
            <span>{option.label}</span>
          </button>
        ))}
      </div>
    </div>
  );
}

export default FilterDropdown;